import React from 'react';
import { DATA_CHARS_ENABLED } from 'js/constants/config';
import { POLLING_PRESET_INTERVALS } from 'new/constants/pollingConfig';
import SchemaViewer from '../../elements/schema-viewer/SchemaViewer';
import {
  SCHEMA_DISPLAY_LIVE_SAMPLES_DEFAULT,
  SCHEMA_DISPLAY_NUM_SAMPLES_DEFAULT,
} from 'js/constants/schemaEditor';
import { API_SERVER_URL } from 'js/constants/api';
import { getEmptyStateContent } from 'new/utils/emptyStateUtils';
import { isEmpty, has } from 'lodash';
import JSONUtils from 'js/lib/JSONUtils';
import DataSamplesProcessor from 'js/lib/DataSamplesProcessor';
import DataSchemaProcessor from 'js/lib/DataSchemaProcessor';

class DataSetDataBrowser extends React.Component {
  static defaultProps = {
    autoFetchLiveSamples: false,
    compactHeader: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      numSamples: SCHEMA_DISPLAY_NUM_SAMPLES_DEFAULT,
      liveSamples: SCHEMA_DISPLAY_LIVE_SAMPLES_DEFAULT,
      showMeta: false,
      delayGridMounting: true,
      isPollingForLiveSamples: false,
      customSamplesCode: null, // user-edited samples code for testing
    };

    this._gridDisplayTimer = null;
    this._liveSamplesPollingInterval = null;

    this._showGrid = this._showGrid.bind(this);
    this._handleRefreshSamples = this._handleRefreshSamples.bind(this);
    this._handleNumSamplesChanged = this._handleNumSamplesChanged.bind(this);
    this._checkForLiveSamples = this._checkForLiveSamples.bind(this);
    this._handleSamplesEdited = this._handleSamplesEdited.bind(this);
    this._handleResetSamplesClicked = this._handleResetSamplesClicked.bind(this);
    this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
  }

  componentDidMount() {
    this._handleRefreshSamples();

    if (this.state.delayGridMounting) {
      this._gridDisplayTimer = setTimeout(this._showGrid, 1200);
    }

    if (this.props.autoFetchLiveSamples) {
      this._startPollingForLiveSamples();
    }

    // Add visibility change listener for smart polling (pause when tab is hidden)
    document.addEventListener('visibilitychange', this._handleVisibilityChange);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.numSamples !== this.state.numSamples ||
      prevState.liveSamples !== this.state.liveSamples ||
      prevProps.data.id !== this.props.data.id
    ) {
      this._handleRefreshSamples();
      // Reset custom samples when dataset changes
      if (prevProps.data.id !== this.props.data.id) {
        this.setState({ customSamplesCode: null });
        if (this.props.onSamplesChanged) {
          this.props.onSamplesChanged(null);
        }
      }
    }

    // Handle autoFetchLiveSamples changes
    if (prevProps.autoFetchLiveSamples !== this.props.autoFetchLiveSamples) {
      if (this.props.autoFetchLiveSamples) {
        // Start polling if changed from false to true
        this._startPollingForLiveSamples();
      } else {
        // Clean up everything if changed from true to false
        this._stopPollingForLiveSamples();
        // Reset any related state
        this.setState({
          isPollingForLiveSamples: false,
          liveSamples: SCHEMA_DISPLAY_LIVE_SAMPLES_DEFAULT,
        });
      }
    }
  }

  componentWillUnmount() {
    if (this._gridDisplayTimer) {
      clearTimeout(this._gridDisplayTimer);
      this._gridDisplayTimer = null;
    }
    this._stopPollingForLiveSamples();
    document.removeEventListener('visibilitychange', this._handleVisibilityChange);
  }

  _startPollingForLiveSamples() {
    if (!this.state.isPollingForLiveSamples) {
      this.setState({ isPollingForLiveSamples: true });
      this._checkForLiveSamples();
    }
  }

  _stopPollingForLiveSamples() {
    if (this._liveSamplesPollingInterval) {
      clearTimeout(this._liveSamplesPollingInterval);
      this._liveSamplesPollingInterval = null;
    }
    this.setState({ isPollingForLiveSamples: false });
  }

  _handleVisibilityChange() {
    if (document.hidden) {
      // Tab is hidden - pause polling to reduce server load
      if (this._liveSamplesPollingInterval) {
        clearTimeout(this._liveSamplesPollingInterval);
        this._liveSamplesPollingInterval = null;
      }
    } else {
      // Tab is visible - resume polling if it was active
      if (this.state.isPollingForLiveSamples && this.props.autoFetchLiveSamples) {
        this._checkForLiveSamples();
      }
    }
  }

  async _checkForLiveSamples() {
    // This is a rare edge case for syslog detected datasets only
    // Most users will never hit this code path
    if (!this.props.autoFetchLiveSamples) {
      return;
    }

    const { data, fetchDataSamples, postSampleAndSchema, fetchSchema } = this.props;

    try {
      // 1. Fetch live samples - we need to await this to check the result
      console.log(`fetching live samples for ${data.id}`);
      await fetchDataSamples(data.id, 5, true);

      // 2. Check if we have samples
      if (this.props.dataSetSamples?.nestedSamples?.length > 0) {
        // Stop polling immediately to prevent any further operations
        this._stopPollingForLiveSamples();

        console.log(
          `found ${this.props.dataSetSamples?.nestedSamples?.length} live samples for ${data.id}`,
        );
        const sampleToConvert = this.props.dataSetSamples?.nestedSamples;

        // 3. Convert sample to schema using direct API call
        try {
          const payload = {
            input: sampleToConvert,
            include_metadata: 1,
            transform: {
              version: 1,
              data_maps: [],
              transforms: [],
            },
          };

          const response = await fetch(`${API_SERVER_URL}/transform?accumulate_schema=1`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.props.accessToken}`,
            },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            throw new Error('Failed to convert sample to schema');
          }

          const convertedSchema = await response.json();

          //if convertedSchema has schemas[0] then we can pick this up, else no schema, keep going. check for schemas being present, being an array and having a length > 0
          const schema = convertedSchema?.schemas?.[0];
          if (schema) {
            const datasetPayload = {
              source_schema: schema,
              output_schema: schema,
              data_samples: sampleToConvert,
            };
            //update the dataset?
            const dsResponse = await fetch(`${API_SERVER_URL}/data_sets/${data.id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.props.accessToken}`,
              },
              body: JSON.stringify(datasetPayload),
            });

            if (!dsResponse.ok) {
              throw new Error('Failed to update dataset');
            }
            this.props.loadDataSet(data.id);
          } else {
            console.log('no schema found');
          }
        } catch (error) {
          console.error('Error converting sample to schema:', error);
          // Even if this fails, we should stop polling
          this._stopPollingForLiveSamples();
        }
      } else {
        // If no samples, wait and try again using LIVE_SAMPLES preset interval (20s)
        // But only if autoFetchLiveSamples is still true
        if (this.props.autoFetchLiveSamples) {
          this._liveSamplesPollingInterval = setTimeout(
            this._checkForLiveSamples,
            POLLING_PRESET_INTERVALS.LIVE_SAMPLES,
          );
        }
      }
    } catch (error) {
      console.error('Error in live samples polling:', error);
      // Always stop polling on error
      this._stopPollingForLiveSamples();
    }
  }

  _showGrid() {
    this.setState({ delayGridMounting: false });
  }

  _handleRefreshSamples() {
    const { data, fetchCharacteristics, fetchDataSamples } = this.props;

    fetchDataSamples(data.id, this.state.numSamples, this.state.liveSamples);

    if (DATA_CHARS_ENABLED) {
      fetchCharacteristics(data.id);
    }
  }

  _handleNumSamplesChanged(optionValue) {
    const { count, live } = optionValue;

    this.setState({
      numSamples: count,
      liveSamples: live,
    });
  }

  _handleShowMetaChanged = (showMeta) => this.setState({ showMeta });

  _handleSamplesEdited(value) {
    if (value === 'Loading...') return;
    this.setState({ customSamplesCode: value });

    if (this.props.onSamplesChanged) {
      try {
        const parsedSamples = JSONUtils.parseJson(value);
        if (Array.isArray(parsedSamples)) {
          this.props.onSamplesChanged(parsedSamples);
        } else {
          this.props.onSamplesChanged(null);
        }
      } catch (error) {
        this.props.onSamplesChanged(null);
      }
    }
  }

  _handleResetSamplesClicked() {
    this.setState({ customSamplesCode: null });
    if (this.props.onSamplesChanged) {
      this.props.onSamplesChanged(null);
    }
  }

  render() {
    const { numSamples, liveSamples, showMeta } = this.state;
    const { data } = this.props;
    const nodeType = data?.node_type || 'data_set';

    const shouldShowLoadingState = this.state.delayGridMounting || this.props?.isLoadingSamples;

    // Check if this is an API request node type (prioritize samples)
    const isApiRequestNode = [
      'api_request',
      'api_result',
      'api_error',
      'api_success_modifier',
      'splitter_output',
    ].includes(nodeType);

    // Check output_schema from either data.output_schema.properties or processed dataSetSchema.out
    // Empty arrays/objects mean no schema
    // isEmpty() handles null/undefined/empty checks
    const hasOutputSchema =
      (data?.output_schema?.properties && !isEmpty(data.output_schema.properties)) ||
      (this.props.dataSetSchema?.out && !isEmpty(this.props.dataSetSchema.out));

    // Check if samples are available
    const hasSamples =
      this.props.dataSetSamples?.nestedSamples?.length > 0 ||
      this.props.dataSetSamples?.samples?.length > 0;

    // For API request nodes, prioritize samples - show table if samples exist, even without schema
    // For other nodes, require schema to show table
    let shouldShowEmptyState = false;
    if (!shouldShowLoadingState) {
      if (isApiRequestNode) {
        // For API requests: only show empty state if both schema and samples are missing
        shouldShowEmptyState = !hasOutputSchema && !hasSamples;
      } else {
        // For regular datasets: only show empty state if schema is missing
        shouldShowEmptyState = !hasOutputSchema;
      }
    }

    const emptyStateContent = shouldShowEmptyState ? getEmptyStateContent(nodeType) : null;

    // Handle custom samples when allowEditing is enabled (for API server flows)
    let effectiveNestedSamples = this.props.dataSetSamples?.nestedSamples;
    let effectiveSamples = this.props.dataSetSamples?.samples;
    let effectiveSchema = this.props.dataSetSchema?.out;
    let effectiveMetaSchema = this.props.dataSetSchema?.outMetaData;

    if (this.props.allowEditing && this.state.customSamplesCode) {
      try {
        const parsedSamples = JSONUtils.parseJson(this.state.customSamplesCode);
        if (Array.isArray(parsedSamples)) {
          // When users edit samples in CODE view, they might be editing:
          // 1. Raw data objects (the rawMessage content) - need to wrap in rawMessage
          // 2. Full sample objects with rawMessage property - use as-is
          // Check the first sample to determine the format
          const firstSample = parsedSamples[0];
          const needsRawMessageWrapper =
            firstSample &&
            typeof firstSample === 'object' &&
            !firstSample.rawMessage &&
            !firstSample.nexlaMetaData;

          // Format samples to match expected structure
          const formattedSamples = needsRawMessageWrapper
            ? parsedSamples.map((sample) => ({ rawMessage: sample }))
            : parsedSamples;

          effectiveNestedSamples = formattedSamples;
          // Generate flat samples for TABLE view
          effectiveSamples = formattedSamples.map((sample) =>
            DataSamplesProcessor.flattenSamples(sample),
          );

          // If schema is missing, generate it from the samples for TABLE view
          // We can generate from either raw samples or flat samples' attrs
          if (!effectiveSchema || isEmpty(effectiveSchema)) {
            try {
              // Try generating from raw samples first
              const rawSamplesForSchema = formattedSamples.map(
                (sample) => sample.rawMessage || sample,
              );

              // Generate schema from raw samples
              effectiveSchema = DataSchemaProcessor.getSchemaFromSamples(
                rawSamplesForSchema,
                false,
              );
              effectiveMetaSchema = DataSchemaProcessor.getSchemaFromSamples(
                rawSamplesForSchema,
                true,
              );

              // If that didn't work, try generating from flat samples' attrs
              if (!effectiveSchema || isEmpty(effectiveSchema)) {
                const attrsSamples = effectiveSamples.map((sample) => sample.attrs || {});
                effectiveSchema = DataSchemaProcessor.getSchemaFromSamples(attrsSamples, false);
                effectiveMetaSchema = DataSchemaProcessor.getSchemaFromSamples(attrsSamples, true);
              }
            } catch (schemaError) {
              console.error('Failed to generate schema from samples:', schemaError);
            }
          }
        }
      } catch (error) {
        console.warn('Failed to parse custom samples code:', error);
      }
    }

    // Fallback: Generate schema from samples if schema is missing but samples exist
    // This ensures TABLE view works even when schema isn't available
    if (
      (!effectiveSchema || isEmpty(effectiveSchema)) &&
      effectiveSamples &&
      effectiveSamples.length > 0
    ) {
      try {
        const attrsSamples = effectiveSamples.map((sample) => sample.attrs || {});
        if (attrsSamples.length > 0 && attrsSamples[0] && Object.keys(attrsSamples[0]).length > 0) {
          effectiveSchema = DataSchemaProcessor.getSchemaFromSamples(attrsSamples, false);
          effectiveMetaSchema = DataSchemaProcessor.getSchemaFromSamples(attrsSamples, true);
        }
      } catch (schemaError) {
        console.warn('Failed to generate schema from flat samples attrs:', schemaError);
      }
    }

    return (
      <div className="tab-content preview-tab">
        <SchemaViewer
          dataSet={this.props.data}
          schema={effectiveSchema}
          metaSchema={effectiveMetaSchema}
          semanticSchema={this.props.dataSetSchema?.semanticSchema}
          samples={effectiveSamples}
          nestedSamples={effectiveNestedSamples}
          numSamples={numSamples}
          liveSamples={liveSamples}
          showMeta={showMeta}
          defaultViewMode={this.props.defaultViewMode}
          darkEditor={this.props.darkEditor}
          onRefreshSamples={this._handleRefreshSamples}
          characteristics={this.props.dataSetSamples?.characteristics}
          showCharacteristics
          samplesUpdatedAt={this.props.dataSetSamples?.lastUpdated}
          isLoadingSamples={shouldShowLoadingState}
          compactHeader={this.props.compactHeader}
          onNumSamplesChanged={this._handleNumSamplesChanged}
          onShowMetaChanged={this._handleShowMetaChanged}
          shouldShowEmptyState={shouldShowEmptyState}
          emptyStateContent={emptyStateContent}
          allowEditing={this.props.allowEditing}
          customSamplesCode={this.state.customSamplesCode}
          onEdit={this._handleSamplesEdited}
          onResetClicked={this._handleResetSamplesClicked}
        />
      </div>
    );
  }
}

export default DataSetDataBrowser;
